import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ContactService } from '../../_services/api/public/contact.service';
import { ContactCreate, ValidationPatterns } from '../../_models/contact.model';
import { ApiResponse } from '../../_models/api-response.model';

@Component({
  selector: 'app-public-contact',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './public-contact.component.html',
  styleUrl: './public-contact.component.scss',
})
export class PublicContactComponent implements OnInit, OnDestroy {
  contactForm!: FormGroup;
  isLoading = false;
  isSubmitting = false;
  successMessage = '';
  errorMessage = '';
  
  frenchTranslations = {
    required: 'Ce champ est requis',
    fullNameRequired: 'Le nom complet est requis',
    fullNamePattern: 'Veuillez saisir un nom valide (2-50 caractères, lettres, espaces, apostrophes et tirets uniquement)',
    emailRequired: 'L\'email est requis',
    emailInvalid: 'Veuillez saisir un email valide',
    phonePattern: 'Veuillez saisir un numéro de téléphone valide',
    subjectMaxLength: 'Le sujet doit comporter moins de 255 caractères',
    messageRequired: 'Le message est requis',
    messageMaxLength: 'Le message doit comporter moins de 2000 caractères',
    sendingMessage: 'Envoi du message en cours...',
    sendMessage: 'Envoyer le message',
    contactSuccess: 'Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.',
    contactFailed: 'Échec de l\'envoi du message. Veuillez réessayer.',
    loadingForm: 'Chargement du formulaire...',
    createNewContact: 'Créer une nouvelle demande de contact'
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.contactForm = this.fb.group({
      fullName: ['', [
        Validators.required,
        Validators.pattern(ValidationPatterns.name)
      ]],
      email: ['', [
        Validators.required,
        Validators.email
      ]],
      phoneNumber: ['', [
        Validators.pattern(ValidationPatterns.phone)
      ]],
      subject: ['', [
        Validators.maxLength(255)
      ]],
      message: ['', [
        Validators.required,
        Validators.maxLength(2000)
      ]]
    });
  }

  onSubmit(): void {
    if (this.contactForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.contactForm);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.contactForm.value;
    
    const contactData: ContactCreate = {
      fullName: formValue.fullName.trim(),
      email: formValue.email.trim(),
      message: formValue.message.trim(),
      phoneNumber: formValue.phoneNumber?.trim() || undefined,
      subject: formValue.subject?.trim() || undefined
    };

    this.contactService.createContact(contactData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.isSubmitting = false;
          this.successMessage = this.frenchTranslations.contactSuccess;
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error.error?.message || this.frenchTranslations.contactFailed;
          console.error('Contact creation error:', error);
        }
      });
  }

  createNewContact(): void {
    this.successMessage = '';
    this.contactForm.reset({
      fullName: '',
      email: '',
      phoneNumber: '',
      subject: '',
      message: ''
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get f() {
    if (!this.contactForm || !this.contactForm.controls) {
      return {};
    }
    return this.contactForm.controls;
  }

  hasError(controlName: string, errorName: string): boolean {
    const control = this.contactForm?.get(controlName);
    return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
  }

  getErrorMessage(controlName: string, errorName: string): string {
    const errorMessages: { [key: string]: string } = {
      'fullName.required': this.frenchTranslations.fullNameRequired,
      'fullName.pattern': this.frenchTranslations.fullNamePattern,
      'email.required': this.frenchTranslations.emailRequired,
      'email.email': this.frenchTranslations.emailInvalid,
      'phoneNumber.pattern': this.frenchTranslations.phonePattern,
      'subject.maxlength': this.frenchTranslations.subjectMaxLength,
      'message.required': this.frenchTranslations.messageRequired,
      'message.maxlength': this.frenchTranslations.messageMaxLength,
    };
    
    return errorMessages[`${controlName}.${errorName}`] || 'Erreur de validation';
  }
}